import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ScanLine, HeartPulse, ShowerHead, QrCode } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import KpiCard from '../components/ui/KpiCard';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import DataTable from '../components/ui/DataTable';
import CommandCenterService from '../services/CommandCenterService';
import PatientService from '../services/PatientService';

export default function PatientApp() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const commandResponse = await CommandCenterService.getStats();
        const activeIncident = commandResponse?.data?.activeIncident || null;

        if (activeIncident) {
          const { entity } = await PatientService.findByAnnouncement(activeIncident.id);
          setPatients(entity || []);
        } else {
          setPatients([]);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    load();
    const intervalId = setInterval(load, 15000);
    return () => clearInterval(intervalId);
  }, []);

  const redCount = patients.filter((p) => p.triage === 'Red').length;
  const deconPendingCount = patients.filter((p) => p.decon !== 'Complete').length;
  const handoffReadyCount = patients.filter((p) => p.destination && p.destination !== 'Pending' && p.cbrne?.includes('Clear')).length;

  const kpis = [
    { label: 'Registered', value: patients.length, note: 'Unique ID, no personal data shown', icon: ScanLine, type: 'blue' },
    { label: 'Red Zone', value: redCount, note: 'Immediate clinical visibility', icon: HeartPulse, type: 'red' },
    { label: 'Decon Pending', value: deconPendingCount, note: 'Awaiting decontamination', icon: ShowerHead, type: 'amber' },
    { label: 'QR/NFC Ready', value: handoffReadyCount, note: 'Ambulance transfer package', icon: QrCode, type: 'green' },
  ];

  const headCells = [
    { id: 'id', label: 'Patient ID' },
    { id: 'triage', label: 'Triage', render: (row) => <Badge>{row.triage}</Badge> },
    { id: 'zone', label: 'Zone' },
    { id: 'vitals', label: 'Vitals' },
    { id: 'cbrne', label: 'CBRNE Result' },
    { id: 'decon', label: 'Decon', render: (row) => <Badge>{row.decon}</Badge> },
    { id: 'destination', label: 'Destination' },
    { id: 'sync', label: 'Sync', render: (row) => <Badge>{row.sync}</Badge> },
  ];

  return (
    <Sidebar title="Patient Documentation">
      <section className="space-y-6">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {kpis.map((k) => (
            <KpiCard key={k.label} {...k} />
          ))}
        </div>

        <div className="bg-white rounded-xl border border-neutral-200 shadow-subtle overflow-hidden dark:bg-ink-900 dark:border-neutral-700">
          <div className="px-6 py-4 border-b border-neutral-100 flex justify-between items-center dark:border-neutral-800">
            <h3 className="font-bold text-neutral-800 dark:text-neutral-100">Patient Documentation Dashboard</h3>
            <div className="flex gap-2">
              <Button onClick={() => toast.success('QR/NFC handoff package created.')}>Generate QR/NFC</Button>
            </div>
          </div>
          <DataTable
            headCells={headCells}
            rows={patients}
            loading={loading}
            onView={(row) => navigate(`/patient/${row.recordId}`)}
            emptyLabel={loading ? 'Loading patients…' : 'No patients registered for the active incident.'}
          />
        </div>
      </section>
    </Sidebar>
  );
}
